'use client'

import React, { useEffect, useRef, useState } from 'react'
import {
  Box,
  Text,
  Flex,
  Button,
  Image,
  Tabs,
  TabList,
  Tab,
  Link,
  useBreakpointValue,
} from '@chakra-ui/react'
import { BottomNav } from '@/components/ui/bottom-nav'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ScriptableContext,
  ChartData,
} from 'chart.js'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
)

import { Line } from 'react-chartjs-2'
import RedeemAndVerificationFlow from '@/components/breakdown-popups/redeem-verification-flow'
import { useUser } from '@/src/store/user-store'
import { useWalletBalanceQuery } from '@/src/queries/earnings/wallet-balance'
import { format } from 'date-fns'
import { Icons } from '@/components/icons'
import { Input } from '@/components/ui/input'
import EarningAds from '../earnings/modals/earning-on-ads'
import LykcoinUsdollarToggle from '../earnings/common-ui/lykcoin-usdollar-toggle'
import Calendar from '../earnings/common-ui/calendar'
import EarningTabsCreator from './earning-tabs'
import WatchtimeDropdown from './modal-section/watchtime-dropdown'
import NewCreator from '@/components/new-creator'
import { useEarningCreatorTimeSeries } from '@/src/queries/earnings/creator-graph'
import RewardsGuestView from '../earnings/common-ui/guest-view'
import SortByCreator from './modal-section/sort-by'

const options = {
  maintainAspectRatio: false,
  responsive: true,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      enabled: true,
      intersect: false,
    },
  },
  elements: {
    line: {
      tension: 0,
      borderWidth: 2,
      borderColor: '#24BFA3',
      fill: 'start',
      backgroundColor: '#24BFA320',
    },
    point: {
      radius: 0,
      hitRadius: 5,
    },
  },
}

export default function CreatorAnalytics() {
  const [graphIndex, setGraphIndex] = useState(0)

  const today = new Date()
  const { user, _hasHydrated } = useUser()
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0)
  const [startDate, setStartDate] = useState<Date>(startOfMonth)
  const [endDate, setEndDate] = useState<Date>(endOfMonth)

  const walletBalance = useWalletBalanceQuery()
  const [searchTitle, setSearchTitle] = useState('')
  const [launchFlow, setLaunchFlow] = useState(false)
  const [sortByType, setSortByType] = useState<SortByType>('')
  const selectedSortTypes = sortByType

  const [showInput, setShowInput] = useState<boolean>(false)
  const [showAmount, setShowAmount] = useState(true)
  const [selectedRange, setSelectedRange] = useState<{
    startDate: Date
    endDate: Date
  } | null>({ startDate: startOfMonth, endDate: endOfMonth })
  const inputRef = useRef<HTMLDivElement>(null)
  const [tabAllEarningIndex, setTabAllEarningIndex] = useState(0)
  const isSmallScreen = useBreakpointValue({ base: true, sm: false })

  const tabAllEarnings = isSmallScreen
    ? [{ label: 'Table' }, { label: 'Graph' }]
    : [{ label: 'Table view' }, { label: 'Graph view' }]

  const placeholderText = useBreakpointValue({
    base: 'Title or Description',
    sm: 'Search for Title of any video',
  })

  const getButtonLabel = () => {
    if (!selectedRange || !selectedRange.startDate || !selectedRange.endDate)
      return 'MMM dd, yyyy'

    const { startDate, endDate } = selectedRange

    return `${format(startDate, 'MMM dd, yyyy')} - ${format(endDate, 'MMM dd, yyyy')}`
  }

  type SortByType =
    | ''
    | 'latest'
    | 'oldest'
    | 'most_view'
    | 'least_view'
    | 'high_watch'
    | 'low_watch'
    | 'high_coin'
    | 'low_coin'
    | 'most_like'
    | 'least_like'
    | 'least_comment'
    | 'most_comment'

  type FilterType = {
    earnings: boolean
    watchtime: boolean
    views: boolean
    subscriber: boolean
  }

  const [filterType, setFilterType] = useState<FilterType>({
    earnings: true,
    views: false,
    watchtime: false,
    subscriber: false,
  })

  const activeFilter =
    Object.keys(filterType).find(
      (key) => filterType[key as keyof FilterType]
    ) || ''

  const chartRef = useRef(null)
  const { data }: any =
    startDate && endDate
      ? useEarningCreatorTimeSeries(
          graphIndex,
          startDate,
          endDate,
          activeFilter
        )
      : { data: [], isLoading: false, error: null }

  // const chartData: ChartData<'line', number[], string> = {
  //   labels:
  //     data?.map(
  //       (item: { viewDate?: string; monthName?: string }) =>
  //         item?.viewDate || item.monthName
  //     ) || [],
  //   datasets: [
  //     {
  //       label: 'Coins',
  //       data: data?.map((item: { lykCoin: number }) => item.lykCoin) || [],
  //       borderColor: '#24BFA3',
  //       tension: 0.4,
  //       backgroundColor: (context: ScriptableContext<'line'>) => {
  //         const { ctx, chartArea } = context.chart
  //         if (!chartArea) {
  //           return undefined
  //         }
  //         const gradient = ctx.createLinearGradient(
  //           chartArea.left,
  //           chartArea.top,
  //           chartArea.left,
  //           chartArea.bottom
  //         )

  //         gradient.addColorStop(0, 'rgba(36, 191, 163, 0.2)')
  //         gradient.addColorStop(1, 'rgba(255, 255, 255, 0.2)')

  //         return gradient
  //       },
  //     },
  //   ],
  // }

  const chartData: ChartData<'line', number[], string> = {
    labels:
      data?.map(
        (item: { viewDate?: string; monthName?: string }) =>
          item?.viewDate || item.monthName
      ) || [],
    datasets: [
      ...Object.entries(filterType)
        .filter(([key, isSelected]) => isSelected)
        .map(([key]) => {
          const labelMap: Record<string, string> = {
            earnings: 'Coins',
            watchtime: 'Watch Time',
            views: 'View Count',
            subscriber: 'Subscriber Count',
          }

          const dataFieldMap: Record<string, string> = {
            earnings: 'lykCoin',
            watchtime: 'watchTime',
            views: 'views',
            subscriber: 'subscriber',
          }

          return {
            label: labelMap[key],
            data:
              data?.map(
                (item: Record<string, number>) => item[dataFieldMap[key]] || 0
              ) || [],
            borderColor: {
              earnings: '#24BFA3',
              watchtime: '#24BFA3',
              views: '#24BFA3',
              subscriber: '#24BFA3',
            }[key],
            tension: 0.4,
            backgroundColor: (context: ScriptableContext<'line'>) => {
              const { ctx, chartArea } = context.chart
              if (!chartArea) {
                return undefined
              }
              const gradient = ctx.createLinearGradient(
                chartArea.left,
                chartArea.top,
                chartArea.left,
                chartArea.bottom
              )

              gradient.addColorStop(0, 'rgba(36, 191, 163, 0.2)')
              gradient.addColorStop(1, 'rgba(255, 255, 255, 0.2)')

              return gradient
            },
          }
        }),
    ],
  }

  const handleCreatorEarningsChange = (value: FilterType) => {
    setFilterType(value)
  }

  const handleAllEarningsSortBy = (value: SortByType) => {
    setSortByType(value)
  }

  const handleDate = (startDate: Date, endDate: Date) => {
    setGraphIndex(0)
    setStartDate(startDate)
    setEndDate(endDate)
    setSelectedRange({ startDate, endDate })
  }

  const handleSortChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTitle(e.target.value)
  }

  const handleTabChange = (isLykCoins: boolean) => {
    setShowAmount(isLykCoins)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowInput(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  if (!user && _hasHydrated) {
    return <RewardsGuestView />
  }

  return (
    <>
      <Flex justify={'flex-end'} minH="100vh">
        <RedeemAndVerificationFlow
          launchFlow={launchFlow}
          launchFlowType="redeem"
          setLaunchFlow={setLaunchFlow}
        />

        <Box w="100%" zIndex={2} pb={[20, 0, 10]}>
          <Box pb={10} px={[4, 4, 4, 4]}>
            <Flex
              justifyContent={'flex-start'}
              alignItems={'center'}
              mb={2.5}
              mt={2}
            >
              <Image
                boxSize="30px"
                src={'/rewards/chevron-left.svg'}
                alt="Icon"
              />
              <Link href="/earnings">
                <Text
                  opacity={0.7}
                  fontWeight={600}
                  fontSize={16}
                  color={'#2A2A2A'}
                >
                  Go back
                </Text>
              </Link>
            </Flex>

            <Flex className="items-center justify-between">
              <Text
                fontSize={24}
                className="hidden items-center font-semibold text-[#656565] sm:block"
              >
                Creator Analytics
              </Text>

              <Text
                fontSize={20}
                className="block items-center font-semibold text-[#656565] sm:hidden"
              >
                Creator Dashboard
              </Text>

              <Button
                as={Link}
                href="/advancepayment"
                display={{ base: 'none', sm: 'flex' }}
                bg="#24BEA2"
                color="white"
                fontWeight={600}
                minH="40px"
                px={6}
                borderRadius="10px"
                _hover={{ bg: '#24BEA2', textDecoration: 'none' }}
              >
                Advance Payment Details
              </Button>
            </Flex>

            <Flex
              display={{ base: 'flex', sm: 'none' }}
              className="mt-3 w-full items-center justify-between gap-3"
            >
              <Button
                as={Link}
                href="/advancepayment"
                bg="#24BEA2"
                color="white"
                fontSize={{ base: '13px', sm: '16px' }}
                fontWeight={{ base: 500, sm: 600 }}
                flexShrink={0}
                minH={{ base: '40px', sm: '46px' }}
                px={{ base: 3, sm: 4 }}
                py={{ base: 2, sm: 3 }}
                borderRadius="5px"
                _hover={{ bg: '#24BEA2', textDecoration: 'none' }}
              >
                Advance Payment Details
              </Button>

              <Flex className="flex-shrink-0 justify-end">
                <LykcoinUsdollarToggle onTabChange={handleTabChange} />
              </Flex>
            </Flex>

            {/* row1 */}
            <Flex className="my-4 w-full items-center justify-between max-sm:mt-3">
              <Flex className="w-fit gap-5 sm:w-full">
                <Text
                  fontSize={{ base: 18, sm: 20 }}
                  className="w-fit items-center font-semibold text-[#656565]"
                >
                  All Time
                </Text>

                <div className="hidden sm:block">
                  <LykcoinUsdollarToggle onTabChange={handleTabChange} />
                </div>
              </Flex>

              <Flex gap={4} className="w-fit items-end justify-end sm:w-full">
                <Flex className="items-center justify-center gap-3 rounded-xl border-2 border-[#D3D3D3] bg-[#D0D0D033] px-4 py-1 font-bold text-[#3E3E3E] sm:font-semibold">
                  <Text className="max-sm:text-[12px]">1 USD$</Text>
                  <Image src="/rewards/compare.svg" alt="compare" boxSize={4} />

                  <Flex className="items-center gap-1">
                    <Image
                      boxSize={7}
                      src="/logo-color-circle.svg"
                      alt="Lykstage"
                    />
                    <Text className="max-sm:text-[12px]">50 LYK Coins</Text>
                  </Flex>
                </Flex>
              </Flex>
            </Flex>

            {/* row2 */}
            <EarningTabsCreator showAmount={showAmount} />

            {/* row3 */}
            <Flex
              className={`${
                tabAllEarningIndex === 1
                  ? 'flex-row items-center justify-between max-sm:flex-wrap'
                  : 'flex-col'
              }`}
            >
              <Flex
                className={`${
                  tabAllEarningIndex === 1 ? 'w-full max-sm:flex-wrap' : ''
                } mb-3 mt-6 justify-between`}
              >
                <Flex className="w-full gap-5 max-sm:justify-between">
                  <Text className="items-center text-2xl font-semibold text-[#656565]">
                    Recent Content
                  </Text>

                  <Tabs
                    index={tabAllEarningIndex}
                    onChange={(index) => setTabAllEarningIndex(index)}
                    variant="soft-rounded"
                    colorScheme="green"
                    size="sm"
                  >
                    <TabList className="rounded-full border-2 border-[#D3D3D3] p-0.5">
                      {tabAllEarnings.map((tab, index) => (
                        <Tab
                          key={index}
                          bg={
                            tabAllEarningIndex === index ? '#24BFA3' : '#FFFFFF'
                          }
                          color={
                            tabAllEarningIndex === index ? '#FFFFFF' : '#9F9F9F'
                          }
                          _selected={{ bg: '#24BFA3', color: '#FFFFFF' }}
                        >
                          {tab.label}
                        </Tab>
                      ))}
                    </TabList>
                  </Tabs>
                </Flex>

                {tabAllEarningIndex === 1 && (
                  <Flex className="max-sm:mt-4">
                    <WatchtimeDropdown
                      filterType={filterType}
                      setFilterType={setFilterType}
                      handleCreatorEarningsChange={handleCreatorEarningsChange}
                    />

                    {/* Calendar */}
                    <Box
                      className={`${
                        tabAllEarningIndex === 1 ? 'ml-4 sm:hidden' : ''
                      } relative`}
                    >
                      <Button
                        borderColor={'#D3D3D3 !important'}
                        borderWidth={'2px !important'}
                        borderStyle={'solid !important'}
                        textDecoration={'none'}
                        minW={'auto'}
                        leftIcon={
                          <Image
                            src="/rewards/breakdown/calendarIcon.svg"
                            height={4}
                            width={4}
                          />
                        }
                        borderRadius={'6px !important'}
                        className="rounded-md no-underline"
                        px={{
                          base: '5px !important',
                          sm: '18px !important',
                          xl: '14px !important',
                        }}
                        py={{ base: '12px !important', sm: 5 }}
                        color="#656565"
                        bg="white"
                        _hover={{ bg: 'transparent' }}
                        fontWeight="400"
                        size={{ base: 'xs', sm: 'md' }}
                        onClick={() => setShowInput((prev) => !prev)}
                      >
                        {getButtonLabel()}
                      </Button>

                      {showInput && (
                        <>
                          <Box
                            display={{ base: 'block', sm: 'none' }}
                            position="fixed"
                            top="0"
                            left="0"
                            width="100vw"
                            height="100vh"
                            bg="rgba(0, 0, 0, 0.5)"
                            zIndex="overlay"
                          />
                          <Flex className="flex-row gap-2">
                            <Flex
                              ref={inputRef}
                              position={{ base: 'fixed', sm: 'absolute' }}
                              top={{ base: '50%', sm: '100%' }}
                              left={{ base: '50%', sm: 'unset' }}
                              mt={{ base: '0', sm: 2 }}
                              transform={{
                                base: 'translate(-50%, -50%)',
                                sm: 'none',
                              }}
                              right={{
                                base: 'none',
                                sm: '0',
                              }}
                              bg="white"
                              boxShadow="0 4px 6px rgba(0, 0, 0, 0.1)"
                              borderRadius="md"
                              zIndex="tooltip"
                              p={2}
                            >
                              <Calendar
                                handleDate={handleDate}
                                defaultRange={{
                                  startDate: startDate,
                                  endDate: endDate,
                                }}
                              />
                            </Flex>
                          </Flex>
                        </>
                      )}
                    </Box>
                  </Flex>
                )}

                {tabAllEarningIndex === 0 && (
                  <Button
                    bg={'#ED837F'}
                    href="/creator-analytics/all-videos"
                    as={Link}
                    color={'white'}
                    display={{ base: 'none', sm: 'flex' }}
                    justifyContent={'center'}
                    size={'md'}
                    px={7}
                    _hover={{ bg: '#ED837F', textDecoration: 'none' }}
                  >
                    View All Videos
                  </Button>
                )}
              </Flex>

              <Flex
                className={`${
                  tabAllEarningIndex === 1 ? 'mb-3 mt-6' : ''
                } flex-wrap items-center justify-between gap-3 sm:flex-nowrap`}
              >
                {/* Search */}
                {tabAllEarningIndex === 0 && (
                  <div
                    className={`flex w-full items-center overflow-hidden rounded-full border-2 px-4 opacity-75 focus:outline-none focus:ring-0`}
                  >
                    <Input
                      type="text"
                      value={searchTitle}
                      onChange={handleSortChange}
                      className={`focus-visible:out z-0 h-8 flex-1 border-0 border-[#D3D3D3] pl-0 pr-2 placeholder:text-[14px] placeholder:font-normal focus-within:border-[#D3D3D3] focus:border-0 focus:outline-none focus:ring-0 focus-visible:shadow-none focus-visible:outline-none focus-visible:ring-0 active:border-[#D3D3D3] sm:h-10 `}
                      placeholder={placeholderText}
                      autoComplete="false"
                    />

                    <Icons.Search
                      className={`h-5 w-5 cursor-pointer text-[#656565] lg:h-6 lg:w-6`}
                    />
                  </div>
                )}

                <Flex className="gap-4 max-sm:w-full">
                  {tabAllEarningIndex === 0 && (
                    <Box className="sm:ml-4">
                      <SortByCreator
                        sortByType={sortByType}
                        setSortByType={setSortByType}
                        handleAllEarningsSortBy={handleAllEarningsSortBy}
                      />
                    </Box>
                  )}

                  {/* Calendar */}
                  <Box
                    className={`${
                      tabAllEarningIndex === 1 ? 'max-sm:hidden sm:ml-4' : ''
                    } relative`}
                  >
                    <Button
                      borderColor={'#D3D3D3 !important'}
                      borderWidth={'2px !important'}
                      borderStyle={'solid !important'}
                      textDecoration={'none'}
                      minW={'auto'}
                      leftIcon={
                        <Image
                          src="/rewards/breakdown/calendarIcon.svg"
                          height={4}
                          width={4}
                        />
                      }
                      borderRadius={'6px !important'}
                      className="rounded-md no-underline"
                      px={{
                        base: '5px !important',
                        sm: '18px !important',
                        xl: '14px !important',
                      }}
                      py={{ base: '12px !important', sm: 5 }}
                      color="#656565"
                      bg="white"
                      _hover={{ bg: 'transparent' }}
                      fontWeight="400"
                      size={{ base: 'xs', sm: 'md' }}
                      onClick={() => setShowInput((prev) => !prev)}
                    >
                      {getButtonLabel()}
                    </Button>

                    {showInput && (
                      <>
                        <Box
                          display={{ base: 'block', sm: 'none' }}
                          position="fixed"
                          top="0"
                          left="0"
                          width="100vw"
                          height="100vh"
                          bg="rgba(0, 0, 0, 0.5)"
                          zIndex="overlay"
                        />
                        <Flex className="flex-row gap-2">
                          <Flex
                            ref={inputRef}
                            position={{ base: 'fixed', sm: 'absolute' }}
                            top={{ base: '50%', sm: '100%' }}
                            left={{ base: '50%', sm: 'unset' }}
                            mt={{ base: '0', sm: 2 }}
                            transform={{
                              base: 'translate(-50%, -50%)',
                              sm: 'none',
                            }}
                            right={{
                              base: 'none',
                              sm: '0',
                            }}
                            bg="white"
                            boxShadow="0 4px 6px rgba(0, 0, 0, 0.1)"
                            borderRadius="md"
                            zIndex="tooltip"
                            p={2}
                          >
                            <Calendar
                              handleDate={handleDate}
                              defaultRange={{
                                startDate: startDate,
                                endDate: endDate,
                              }}
                            />
                          </Flex>
                        </Flex>
                      </>
                    )}
                  </Box>
                </Flex>
              </Flex>
            </Flex>

            {tabAllEarningIndex === 0 ? (
              <>
                <NewCreator
                  sreach_text={searchTitle}
                  sortBy={selectedSortTypes}
                  startDate={startDate}
                  endDate={endDate}
                />

                <Button
                  bg={'#24BFA3'}
                  href="/creator-analytics/all-videos"
                  as={Link}
                  color={'white'}
                  display={{ base: 'flex', sm: 'none' }}
                  justifyContent={'center'}
                  size={'md'}
                  px={7}
                  mb={'75px'}
                  _hover={{ bg: '#24BFA3', textDecoration: 'none' }}
                >
                  View All Videos
                </Button>
              </>
            ) : (
              <Box
                bg="white"
                border="1px"
                borderColor="gray.200"
                p={5}
                mt={{ base: 5 }}
                rounded="xl"
                h={'360px'}
                w={'100%'}
                pos={'relative'}
              >
                <Line ref={chartRef} data={chartData} options={options} />
              </Box>
            )}
          </Box>
        </Box>

        <BottomNav />
      </Flex>
    </>
  )
}
